/* Facturas y remitos: la fila común a WhatsApp y mail, en qué orden van, qué
 * es nuevo y qué entra en lo que se buscó.
 *
 * Vive acá y no en la pantalla para poder probarlo solo, con las filas reales
 * y sin levantar React ni Supabase.
 *
 * ── LO QUE LLEGÓ ÚLTIMO, ARRIBA (ticket 451) ────────────────────────────────
 *
 * Nico, 19/10 15:57: «Mandé facturas a WhatsApp y todavía no aparecen en el
 * hub. Más de 30 min». Habían llegado: el bot archivó las cuatro de Würth
 * entre las 15:26 y las 15:31. La lista ponía arriba la FECHA DE LA FACTURA
 * más nueva, y esas cuatro eran del 10/09 al 05/10: quedaron enterradas debajo
 * de todo lo que había llegado en las dos semanas anteriores.
 *
 * El que acaba de mandar algo lo busca arriba, y una factura vieja se manda
 * todo el tiempo —la que apareció en un cajón, la que el proveedor reenvió—.
 * Por eso el orden por defecto es CUÁNDO LLEGÓ, y la fecha de la factura queda
 * como el otro orden, elegible, exactamente como estaba.
 *
 * «Llegó» es lo que la pantalla ya muestra al pie de cada carta: para
 * WhatsApp, cuando el bot la archivó (`creado_en`); para mail, cuando llegó el
 * mail (`fecha_mail`) y, si no se sabe, cuando se guardó.
 */

import { tipoDe } from "./facturasMail";
import { normalizar as sinTildes } from "./buscar";

export const TZ = "America/Argentina/Buenos_Aires";

export const POR_LLEGADA = "llegada";
export const POR_FECHA = "fecha";

export const ORDENES = [
  [POR_LLEGADA, "Lo último que llegó"],
  [POR_FECHA, "Por fecha de la factura"],
];

/* Cuánto dura la marca de «Nuevo». Un día alcanza para que el que mandó algo a
 * la tarde lo encuentre a la mañana siguiente, y no tanto como para que media
 * lista diga «Nuevo» y la marca deje de distinguir. */
export const HORAS_NUEVO = 24;

export const llegoEn = (origen, f) =>
  (origen === "whatsapp" ? f?.creado_en : f?.fecha_mail || f?.creado_en) || null;

/* Una fila común a las dos fuentes: lo justo para filtrar, ordenar y buscar. El
   render lee del original (`raw`) según el origen, para no perder ningún matiz.

   `orden` es la clave del orden por fecha de la factura, la que la pantalla
   usaba antes del ticket 451; `llego`, la del orden por llegada. */
export const fila = (origen, f) => ({
  key: `${origen}-${f.id}`,
  origen,
  id: f.id,
  tipo: origen === "whatsapp" ? (f.tipo || "otro") : tipoDe(f),
  descartado: origen === "whatsapp" ? !!f.anulado_en : false,
  orden:
    (origen === "whatsapp"
      ? f.fecha || f.creado_en
      : f.fecha || f.fecha_mail || f.creado_en) || "",
  llego: llegoEn(origen, f),
  archivo: origen === "whatsapp" ? f.archivo : f.ruta,
  /* SIN TILDES NI DIÉRESIS, Y SIN MAYÚSCULAS, de los dos lados. La misma
     factura llega como «Wurth Argentina SA» por WhatsApp y como «Würth
     Argentina S.A.» por mail —el lector copia lo que dice cada papel—, y
     comparando el texto crudo buscar «Würth» no encontraba las cuatro que Nico
     acababa de mandar. */
  busca: sinTildes(
    [
      f.proveedor, f.numero, f.cuit, f.detalle,
      origen === "whatsapp" ? f.quien_nombre : f.remitente || f.remitente_email,
    ]
      .map((v) => String(v || ""))
      .join(" "),
  ),
  raw: f,
});

export function coincide(r, texto) {
  const t = sinTildes(texto);
  return !t || String(r?.busca || "").includes(t);
}

const ms = (iso) => {
  const t = Date.parse(iso || "");
  return Number.isFinite(t) ? t : null;
};

/* Devuelve una lista nueva; la que recibe no se toca.
 *
 * POR FECHA DE LA FACTURA es la comparación de antes, tal cual —incluida la
 * de texto, que mezcla días con horas cuando el bot no pudo leer la fecha—,
 * para que elegirlo deje cada carta donde estaba.
 *
 * POR LLEGADA compara instantes y no textos: `fecha_mail` y `creado_en` traen
 * distinta cantidad de decimales, y comparadas como texto no siempre ordenan
 * como el reloj. Las que empatan quedan como vinieron. Pasa con las de mail:
 * los adjuntos de un mismo mail llevan el mismo `fecha_mail` (las 5 y 6 de la
 * base), y el servicio las trae con el desempate por `id`. Dos de WhatsApp no
 * empatan en la práctica —`creado_en` lleva microsegundos—; si empataran,
 * quedarían por fecha de la factura, que es como las trae el servicio. Una sin
 * hora de llegada va al fondo. */
export function ordenar(filas, modo = POR_LLEGADA) {
  const copia = [...(filas || [])];
  if (modo === POR_FECHA) {
    return copia.sort((a, b) => String(b.orden).localeCompare(String(a.orden)));
  }
  return copia.sort((a, b) => {
    const ta = ms(a.llego);
    const tb = ms(b.llego);
    if (ta === tb) return 0;
    if (ta === null) return 1;
    if (tb === null) return -1;
    return tb - ta;
  });
}

/* Lo que llegó en el último día. Con el reloj de la compu un poco atrasado lo
   recién llegado queda «en el futuro»: sigue siendo nuevo. */
export function esNuevo(llego, ahora = Date.now()) {
  const t = ms(llego);
  return t !== null && ahora - t < HORAS_NUEVO * 3600 * 1000;
}

/* El día y la hora de Buenos Aires, de UN formateador que se arma acá, una
 * vez. `toLocaleDateString` y compañía, cuando se les pasan opciones, arman uno
 * nuevo en cada llamada, y la pantalla pide varias fechas por carta en cada
 * render: cada tecla en el buscador, cada clic, cada link firmado que llega (de
 * a uno). Con las 105 cartas de hoy, redibujar la lista tardaba el doble.
 *
 * Y lo que se escribe se arma con las partes (año, mes, día, hora, minuto), no
 * partiendo el texto que devuelve el navegador: en Chrome 110, en-CA pasó un
 * tiempo a escribir «10/19/2026» en lugar de «2026-10-19». Tampoco se le pide
 * «dd/mm» a es-AR, que con día y mes solos escribe «14/9». */
const FMT_AR = new Intl.DateTimeFormat("en-CA", {
  timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", hourCycle: "h23",
});

function enAr(t) {
  const p = {};
  for (const { type, value } of FMT_AR.formatToParts(t)) p[type] = value;
  return {
    dia: `${p.year}-${p.month}-${p.day}`,
    ddmm: `${p.day}/${p.month}`,
    hora: `${p.hour}:${p.minute}`,
  };
}

const DIA_MS = 24 * 3600 * 1000;

/* «19/10 15:31», a la hora de Buenos Aires. */
export function cuando(iso) {
  const t = ms(iso);
  if (t === null) return "";
  const { ddmm, hora } = enAr(t);
  return `${ddmm} ${hora}`;
}

/* «hoy 15:31», «ayer 09:05» o «14/09 14:39». Hoy y ayer son los de Buenos
   Aires: a las 22:30 de acá ya es mañana en UTC. */
export function cuandoLlego(iso, ahora = Date.now()) {
  const t = ms(iso);
  if (t === null) return "";
  const { dia, ddmm, hora } = enAr(t);
  if (dia === enAr(ahora).dia) return `hoy ${hora}`;
  if (dia === enAr(ahora - DIA_MS).dia) return `ayer ${hora}`;
  return `${ddmm} ${hora}`;
}

/* ── EL LINK AL PAPEL VENCE, Y LA PANTALLA QUEDA ABIERTA HORAS (ticket 455) ──
 *
 * Nico, 19/10 17:02: «Las facturas de Wurth no puedo ver la imagen». La
 * pantalla había firmado los links a las 15:56 y él tocó «Abrir el papel» a
 * las 16:57: Storage contestó 400 («"exp" claim timestamp check failed») y la
 * pestaña nueva mostró ese error en vez de la factura. Los PDF de Würth no
 * tenían nada raro: eran lo último que había llegado, y los PDF se piden
 * recién al tocarlos (las fotos se bajan apenas se firman).
 *
 * Que el link dure una hora está bien y se queda (el que se pega en un chat se
 * muere solo). Lo que faltaba es que la pantalla se diera cuenta.
 *
 * `vence` es el instante (ms) en que Storage deja de aceptarlo. Cinco minutos
 * antes ya no se usa: entre el clic y el pedido de la pestaña nueva pasa un
 * rato, y un link que vence en el camino es la misma pestaña con el error. Sin
 * `vence`, no hay link que sirva. */
export const MARGEN_LINK_MS = 5 * 60 * 1000;

export function linkVigente(vence, ahora = Date.now()) {
  return Number.isFinite(vence) && ahora < vence - MARGEN_LINK_MS;
}
